//@flow
import React, {useCallback, useState} from 'react';

import {ButtonBase, IconButton, Tooltip} from '@material-ui/core';
import {Delete, Refresh, Send, Stop} from '@material-ui/icons';

import {makeStyles} from '@material-ui/core/styles';
import type {Theme} from '@material-ui/core/styles';
import {colors} from '../../../../../../../../../assets/styles/colors';
import {useAlert} from '../../../../../../../../../elements/Alert/AlertContext';
import {useCurrentConversation} from '../../../../../../utils/conversation';
import {getServiceGradient} from '../../../../../../../constans';
import AudioPlayer from '../../../../../../../../../elements/AudioPlayer';

import type {MessageFormFields} from '../../../../constants';
import InlineLoader from '../../../../../../../../../elements/LoadingSpinner/InlineLoader';

import {useAudioRecorder} from '../../../../../../../../../utils/window-navigator/audio-recorder';

type AudioRecorderWrapProps = {
  onSendMessage: (params: MessageFormFields) => Promise<void>,
  disabled?: boolean,
  onClose: () => void,
};

type AudioRecorderProps = {
  onRefresh: () => void,
} & AudioRecorderWrapProps;

const useStyles = makeStyles((theme: Theme) => ({
  wrapRoot: {
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '.5rem',
    height: '70px',
  },
  root: {
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    gap: '.5rem',
    padding: '.75rem',
    backgroundColor: colors.grey200,
  },
  playerContainer: {
    flexGrow: 1,
    backgroundColor: 'white',
    height: 46,
    widows: '100%',
    borderRadius: '.75rem',
    padding: '.5rem',
    display: 'flex',
    alignItems: 'center',
    gap: '.5rem',
  },
  audioPlayer: {
    background: 'white',
  },
  labelButton: {
    padding: '.25rem',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: 'rgba(0, 0, 0, 0.54)',
    cursor: 'pointer',
    fontSize: '1.5rem',
    borderRadius: '50%',

    '&:hover': {
      color: colors.purple,
    },
  },
  sendButton: {
    padding: '.5rem',
    color: 'white',
    backgroundColor: colors.primary,
    borderRadius: '50%',

    '&:hover': {
      color: 'white',
    },

    '& svg': {
      transform: 'translateX(2px)',
    },
  },
  canvasRecorder: {
    width: '100%',
    height: '100%',
  },
  secondsLabel: {
    minWidth: 32,
    textAlign: 'center',
    display: 'block',
  },
}));

const formatTime = (secs: number) => {
  if (secs === Infinity) return '';
  const minutes = Math.floor(secs / 60);
  const seconds = Math.floor(secs % 60);
  const returnedSeconds = seconds < 10 ? `0${seconds}` : `${seconds}`;
  return `${minutes}:${returnedSeconds}`;
};

function AudioRecorder(props: AudioRecorderProps) {
  const {onClose, disabled, onRefresh} = props;

  //$FlowFixMe
  const {onSendMessage} = props;

  const classes = useStyles();

  const {showAlert} = useAlert();

  const handleAudioError = useCallback(() => {
    const message =
      'Tú navegador no soporta grabación de audio o no tienes un micrófono conectado';

    showAlert(message, {
      title: 'Error',
      severity: 'error',
    });

    onClose();
  }, [onClose, showAlert]);

  const {
    stop,
    recorderState,
    recodingResult,
    elapsedSeconds,
    canvasRef,
  } = useAudioRecorder({
    onError: handleAudioError,
    autoStart: true,
  });
  const {currentConversation} = useCurrentConversation();

  if (!currentConversation) return null;

  const handleStopRecording = () => {
    stop();
  };

  const handleSubmit = () => {
    if (!recodingResult) return;
    onSendMessage({
      type: 'AUDIO',
      files: [recodingResult],
      message: '',
    }).then(() => {
      onClose();
    });
  };

  return (
    <div className={classes.root}>
      <Tooltip placement="top" title="Cancelar grabación">
        <ButtonBase className={classes.labelButton} onClick={onClose}>
          <Delete />
        </ButtonBase>
      </Tooltip>

      {recodingResult && recorderState !== 'recording' && (
        <AudioPlayer
          src={recodingResult}
          disabled={disabled}
          classes={{
            root: classes.audioPlayer,
          }}
        />
      )}
      {recorderState === 'recording' && (
        <div className={classes.playerContainer}>
          <canvas className={classes.canvasRecorder} ref={canvasRef}></canvas>
          <span className={classes.secondsLabel}>
            {formatTime(elapsedSeconds)}
          </span>
        </div>
      )}

      {recorderState === 'recording' && (
        <Tooltip placement="top" title="Detener grabación">
          <ButtonBase
            className={classes.labelButton}
            onClick={handleStopRecording}
            disabled={disabled}
          >
            <Stop />
          </ButtonBase>
        </Tooltip>
      )}

      {recorderState === 'stopped' && (
        <Tooltip placement="top" title="Volvel a grabar">
          <ButtonBase
            className={classes.labelButton}
            onClick={onRefresh}
            disabled={disabled}
          >
            <Refresh />
          </ButtonBase>
        </Tooltip>
      )}

      <IconButton
        size="small"
        type="submit"
        color="primary"
        className={classes.sendButton}
        disabled={disabled || recorderState !== 'stopped' || !recodingResult}
        onClick={handleSubmit}
        style={{
          background:
            currentConversation &&
            getServiceGradient(currentConversation.ravenService),
        }}
      >
        <Send fontSize="small" />
      </IconButton>
    </div>
  );
}

export default function AudioRecorderWrap(props: AudioRecorderWrapProps) {
  const [showAudioForm, setShowAudioForm] = useState(true);

  const classes = useStyles();

  const handleRefresh = () => {
    setShowAudioForm(false);
    setTimeout(() => {
      setShowAudioForm(true);
    }, 500);
  };

  if (showAudioForm)
    return <AudioRecorder {...props} onRefresh={handleRefresh} />;

  return (
    <div className={classes.wrapRoot}>
      <InlineLoader loading size={24} />
    </div>
  );
}
